import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Slideout,
  Typography,
  Button,
  CardViewX,
  Card,
  Avatar,
  Tag,
  Icon,
  CircularProgress,
} from "@ann/web-components";
import { NameInitialsAvatar } from "react-name-initials-avatar";
import {
  getAvatarUrl,
  generateAvatarColor,
} from "../../../../../../../utils/avatarUtils";
import { getDepartmentDetail } from "../../../../services/departmentDetail";
import {
  SlideoutContainer,
  HeaderSection,
  HeaderTitleRow,
  QuickStatsSection,
  QuickStatsGrid,
  QuickStatCard,
  QuickStatIcon,
  QuickStatValue,
  QuickStatLabel,
  AdminOwnerSection,
  AdminHeaderContent,
  KnowledgeBasesDetailsCardContent,
  KnowledgeBasesSection,
  KnowledgeBaseItem,
  DetailsSection,
  DetailRow,
  ActivitySection,
  ActivityCard,
  ActivityCardLeft,
  AvatarWrapper,
  LoadingContainer,
  ErrorContainer,
} from "./DepartmentDetailSlideout.styled";

const formatDate = (dateString) => {
  if (!dateString) return "Unknown";
  try {
    return new Date(dateString).toISOString().split("T")[0];
  } catch {
    return "Unknown";
  }
};

const getValue = (obj, ...keys) => {
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) {
      return obj[key];
    }
  }
  return null;
};

const DepartmentDetailSlideout = ({ open, onClose, department }) => {
  const navigate = useNavigate();
  const [departmentDetail, setDepartmentDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState(null);

  const departmentUuid = getValue(
    department,
    "departmentUuid",
    "department_uuid",
    "department_id",
    "id"
  );

  useEffect(() => {
    if (open && departmentUuid) {
      const fetchDetail = async () => {
        setLoadingDetail(true);
        setError(null);
        try {
          const response = await getDepartmentDetail(departmentUuid);
          if (response?.status_code === 200 && response?.body) {
            setDepartmentDetail(response.body);
          } else {
            setDepartmentDetail(null);
          }
        } catch (err) {
          console.error("Error fetching department detail:", err);
          setError(err);
          setDepartmentDetail(null);
        } finally {
          setLoadingDetail(false);
        }
      };

      fetchDetail();
    } else if (!open) {
      setDepartmentDetail(null);
      setError(null);
    }
  }, [open, departmentUuid]);

  if (!department) return null;

  const detailData = departmentDetail || department;

  const departmentId =
    getValue(detailData, "departmentUuid", "department_id", "id", "department_uuid", "name") || "";
  const departmentName =
    getValue(detailData, "departmentName", "department_name", "name") || "Unnamed Department";
  const ownerName = getValue(detailData, "ownerName", "owner_name") || "";
  const userCount = getValue(detailData, "userCount", "user_count", "totalActiveMembers", "total_active_members") ?? 0;
  const description =
    getValue(detailData, "description", "department_description") ||
    "No description available.";
  const photoUrl = getValue(
    detailData,
    "ownerPhotoUrl",
    "profilePhotoUrl",
    "owner_photo_url",
    "photo_url"
  );
  const knowledgeBases =
    getValue(detailData, "knowledgeBase", "knowledgeBases", "knowledge_base", "knowledge_bases") || [];
  const status = getValue(detailData, "status") || "ACTIVE";
  const createdAt = getValue(detailData, "createdAt", "created_at");
  const updatedAt =
    getValue(detailData, "updatedAt", "updated_at", "last_updated") || createdAt;
  const agentWorkflows = getValue(detailData, "agentWorkflows", "agent_workflows") ?? 0;
  const overrides = getValue(detailData, "overrides") ?? 0;
  const feedbackItems = getValue(detailData, "feedbackItems", "feedback_items") ?? 0;

  const avatarUrl = getAvatarUrl(photoUrl, ownerName);
  const avatarColor = generateAvatarColor(ownerName);
  const hasAvatar = !!avatarUrl;

  const statusColor =
    status === "ACTIVE"
      ? "success"
      : status === "INACTIVE"
      ? "error"
      : "informative";

  const adminRole = getValue(detailData, "ownerRole", "owner_role", "admin_role") || "Department Admin";

  const handleViewDetails = () => {
    onClose();
    navigate(`/v2/departments/${departmentId}/overview`, {
      state: { departmentName, departmentData: department },
    });
  };

  return (
    <Slideout open={open} onClose={onClose} size="large">
      <Slideout.Content>
        {loadingDetail ? (
          <LoadingContainer>
            <CircularProgress size="xl" />
          </LoadingContainer>
        ) : error ? (
          <ErrorContainer>
            <Typography category="body" size="medium" weight="regular" color="error">
              Error loading department details
            </Typography>
          </ErrorContainer>
        ) : (
          <SlideoutContainer>
          <HeaderSection>
            <HeaderTitleRow>
              <Typography category="title" size="large" weight="bold">
                {departmentName}
              </Typography>
              <Tag color={statusColor} size="small" text={status} />
            </HeaderTitleRow>
            <Typography
              category="body"
              size="medium"
              weight="regular"
              color="body"
            >
              {description}
            </Typography>
          </HeaderSection>

          <QuickStatsSection>
            <Typography category="subtitle" size="small" weight="bold">
              Quick Stats
            </Typography>
            <QuickStatsGrid>
              <QuickStatCard>
                <QuickStatIcon>
                  <Icon name="group" size="medium" />
                </QuickStatIcon>
                <QuickStatValue>
                  <Typography category="title" size="large" weight="bold">
                    {userCount}
                  </Typography>
                </QuickStatValue>
                <QuickStatLabel>
                  <Typography
                    category="body"
                    size="small"
                    weight="regular"
                    color="body"
                  >
                    Users
                  </Typography>
                </QuickStatLabel>
              </QuickStatCard>
              <QuickStatCard>
                <QuickStatIcon>
                  <Icon name="stickyNote2" size="medium" />
                </QuickStatIcon>
                <QuickStatValue>
                  <Typography category="title" size="large" weight="bold">
                    {knowledgeBases.length}
                  </Typography>
                </QuickStatValue>
                <QuickStatLabel>
                  <Typography
                    category="body"
                    size="small"
                    weight="regular"
                    color="body"
                  >
                    KBs
                  </Typography>
                </QuickStatLabel>
              </QuickStatCard>
            </QuickStatsGrid>
          </QuickStatsSection>
          <AdminOwnerSection>
            <Typography category="subtitle" size="small" weight="bold">
              Admin Owner
            </Typography>
            <CardViewX size="medium">
              <CardViewX.Header
                title={
                  <AdminHeaderContent>
                    {hasAvatar ? (
                      <Avatar alt={ownerName} size="md" src={avatarUrl} />
                    ) : (
                      <AvatarWrapper>
                        <NameInitialsAvatar
                          name={ownerName || "Unknown"}
                          bgColor={avatarColor}
                          textColor="#ffffff"
                          size="40px"
                          borderRadius="50%"
                          textWeight="bold"
                          textSize="14px"
                          borderWidth="0"
                        />
                      </AvatarWrapper>
                    )}
                    <Typography
                      category="body"
                      size="medium"
                      weight="regular"
                    >
                      {ownerName || "Unknown Admin"}
                    </Typography>
                  </AdminHeaderContent>
                }
              />
              <CardViewX.Meta>
                <Tag color="informative" size="small" text={adminRole} />
              </CardViewX.Meta>
            </CardViewX>
          </AdminOwnerSection>

          {knowledgeBases.length > 0 && (
            <KnowledgeBasesSection>
              <Typography category="subtitle" size="small" weight="bold">
                Knowledge Bases
              </Typography>
              <Card size="medium">
                <KnowledgeBasesDetailsCardContent>
                  {knowledgeBases.map((kb, index) => (
                    <KnowledgeBaseItem key={index}>
                      <Icon name="stickyNote2" size="small" />
                      <Typography
                        category="body"
                        size="medium"
                        weight="regular"
                      >
                        {kb}
                      </Typography>
                    </KnowledgeBaseItem>
                  ))}
                </KnowledgeBasesDetailsCardContent>
              </Card>
            </KnowledgeBasesSection>
          )}

          <DetailsSection>
            <Typography category="subtitle" size="small" weight="bold">
              Details
            </Typography>
            <Card size="medium">
              <KnowledgeBasesDetailsCardContent>
                {createdAt && (
                  <DetailRow>
                    <Typography
                      category="body"
                      size="medium"
                      weight="regular"
                      color="body"
                    >
                      Created
                    </Typography>
                    <Typography category="body" size="medium" weight="regular">
                      {formatDate(createdAt)}
                    </Typography>
                  </DetailRow>
                )}
                <DetailRow>
                  <Typography
                    category="body"
                    size="medium"
                    weight="regular"
                    color="body"
                  >
                    Last Updated
                  </Typography>
                  <Typography category="body" size="medium" weight="regular">
                    {formatDate(updatedAt)}
                  </Typography>
                </DetailRow>
                <DetailRow>
                  <Typography
                    category="body"
                    size="medium"
                    weight="regular"
                    color="body"
                  >
                    Agent Workflows
                  </Typography>
                  <Typography category="body" size="medium" weight="regular">
                    {agentWorkflows}
                  </Typography>
                </DetailRow>
                <DetailRow>
                  <Typography
                    category="body"
                    size="medium"
                    weight="regular"
                    color="body"
                  >
                    Overrides
                  </Typography>
                  <Typography category="body" size="medium" weight="regular">
                    {overrides}
                  </Typography>
                </DetailRow>
              </KnowledgeBasesDetailsCardContent>
            </Card>
          </DetailsSection>

          <ActivitySection>
            <Typography category="subtitle" size="small" weight="bold">
              Activity
            </Typography>
            <ActivityCard>
              <ActivityCardLeft>
                <Icon name="sms" size="small" />
                <Typography category="body" size="medium" weight="regular">
                  Feedback Items
                </Typography>
              </ActivityCardLeft>
              <Typography category="title" size="small" weight="bold">
                {feedbackItems}
              </Typography>
            </ActivityCard>
          </ActivitySection>
          </SlideoutContainer>
        )}
      </Slideout.Content>
      <Slideout.Actions>
        <Button
          label="View Full Department"
          size="medium"
          type="primary"
          iconRight="arrowRight"
          onClick={handleViewDetails}
          disabled={!departmentId}
        />
      </Slideout.Actions>
    </Slideout>
  );
};

export default DepartmentDetailSlideout;
